import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import FollowUpPerformanceReportApi from "../../../../../infrastructure/api/followUpPerformanceReport";
import useGetPageInfo from "../../../../common/hooks/useGetPageInfo";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import EnhancedTable from "../../../../components/shared/table";
import { toJalaliDate, toMiladyDate } from "../../../../middleware/middleware";

const getDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { fromDate: toMiladyDate(from), toDate: toMiladyDate(to) };
};

const initialValues = {
  ...getDefaultDateRange(),
  userId: "",
  followUpType: "",
  callResult: "",
};

const FollowUpPerformanceReportPage = () => {
  const pageInfo = useGetPageInfo();
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(true);
  const [filterValues, setFilterValues] = useState(initialValues);
  const [filterOptions, setFilterOptions] = useState({
    experts: [],
    followUpTypes: [],
    callResults: [],
  });
  const [rows, setRows] = useState([]);

  const headCells = useMemo(
    () => [
      { id: "fullName", label: "کارشناس" },
      { id: "totalFollowUps", label: "کل پیگیری‌ها", type: "number" },
      { id: "phoneCallCount", label: "تماس تلفنی", type: "number" },
      { id: "answeredCount", label: "پاسخ داده", type: "number" },
      { id: "noAnswerCount", label: "پاسخ نداده", type: "number" },
      { id: "needsFollowUpCount", label: "نیازمند پیگیری", type: "number" },
      {
        id: "convertedToNextStepCount",
        label: "تبدیل به مرحله بعد",
        type: "number",
      },
      {
        id: "overdueFollowUpCountDisplay",
        label: "پیگیری عقب‌افتاده",
        type: "chip",
        model: { colorField: "overdueChipColor" },
      },
      {
        id: "lastFollowUpDateDisplay",
        label: "آخرین پیگیری",
      },
    ],
    []
  );

  const tableRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        lastFollowUpDateDisplay: row.lastFollowUpDate
          ? toJalaliDate(row.lastFollowUpDate)
          : "-",
        overdueFollowUpCountDisplay:
          row.overdueFollowUpCount > 0
            ? String(row.overdueFollowUpCount)
            : "0",
        overdueChipColor: row.overdueFollowUpCount > 0 ? "error" : "gray",
      })),
    [rows]
  );

  useEffect(() => {
    const loadFilters = async () => {
      try {
        setFilterLoading(true);
        const response = await FollowUpPerformanceReportApi.getFilterData();
        if (response?.isSuccess) {
          setFilterOptions({
            experts: response.data?.experts || [],
            followUpTypes: response.data?.followUpTypes || [],
            callResults: response.data?.callResults || [],
          });
        }
      } catch (error) {
        toast.error("خطا در دریافت فیلترهای گزارش");
      } finally {
        setFilterLoading(false);
      }
    };
    loadFilters();
  }, []);

  const onClearFilterValues = () => {
    setFilterValues(initialValues);
    setRows([]);
  };

  const buildSearchPayload = () => ({
    fromDate: filterValues.fromDate || null,
    toDate: filterValues.toDate || null,
    userId: filterValues.userId ? Number(filterValues.userId) : null,
    followUpType: filterValues.followUpType
      ? Number(filterValues.followUpType)
      : null,
    callResult: filterValues.callResult ? Number(filterValues.callResult) : null,
  });

  const onGetReportData = async () => {
    try {
      setLoading(true);
      const response = await FollowUpPerformanceReportApi.getReport(
        buildSearchPayload()
      );
      if (response?.isSuccess) {
        setRows(response.data || []);
        if (!response.data?.length) {
          toast.info("داده‌ای برای نمایش یافت نشد");
        }
      } else {
        toast.error(response?.message || "خطا در دریافت گزارش");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "خطا در دریافت گزارش عملکرد پیگیری"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={pageInfo?.title || "گزارش عملکرد پیگیری کارشناسان"}
            action={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={onGetReportData}
                  disabled={loading || filterLoading}
                >
                  جستجو
                  {loading && (
                    <CircularProgress color="primary" size={16} sx={{ ml: 2 }} />
                  )}
                </Button>
                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ mx: 1 }}
                />
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<HighlightOffIcon />}
                  onClick={onClearFilterValues}
                >
                  پاک‌سازی فیلترها
                </Button>
              </Box>
            }
          />
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={3}>
                <JalaliDatePicker
                  label="از تاریخ"
                  fullWidth
                  size="small"
                  value={filterValues.fromDate}
                  onChange={(value) =>
                    setFilterValues((old) => ({
                      ...old,
                      fromDate: value || "",
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <JalaliDatePicker
                  label="تا تاریخ"
                  fullWidth
                  size="small"
                  value={filterValues.toDate}
                  onChange={(value) =>
                    setFilterValues((old) => ({
                      ...old,
                      toDate: value || "",
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>کارشناس</InputLabel>
                  <Select
                    value={filterValues.userId}
                    label="کارشناس"
                    onChange={(e) =>
                      setFilterValues((old) => ({
                        ...old,
                        userId: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">همه</MenuItem>
                    {filterOptions.experts.map((item) => (
                      <MenuItem key={item.id} value={String(item.id)}>
                        {item.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>نوع پیگیری</InputLabel>
                  <Select
                    value={filterValues.followUpType}
                    label="نوع پیگیری"
                    onChange={(e) =>
                      setFilterValues((old) => ({
                        ...old,
                        followUpType: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">همه</MenuItem>
                    {filterOptions.followUpTypes.map((item) => (
                      <MenuItem key={item.id} value={String(item.id)}>
                        {item.title || item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>نتیجه پیگیری</InputLabel>
                  <Select
                    value={filterValues.callResult}
                    label="نتیجه پیگیری"
                    onChange={(e) =>
                      setFilterValues((old) => ({
                        ...old,
                        callResult: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">همه</MenuItem>
                    {filterOptions.callResults.map((item) => (
                      <MenuItem key={item.id} value={String(item.id)}>
                        {item.title || item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mb: 1 }}>
              <Chip
                size="small"
                label="پیش‌فرض: ۳۰ روز اخیر (در صورت خالی بودن تاریخ)"
                variant="outlined"
              />
            </Box>

            <EnhancedTable
              rowKey="userId"
              rows={tableRows}
              headCells={headCells}
              pageSize={20}
              showFilter
              defaultSort={{ orderBy: "totalFollowUps", order: "desc" }}
              getRowStatus={(row) =>
                row.overdueFollowUpCount > 0 ? "error" : ""
              }
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default FollowUpPerformanceReportPage;
